import { ComponentProps, ReactNode } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { css } from '@flipster/styled-system/css';
import { vstack } from '@flipster/styled-system/patterns';

import { SparkRewardBanner } from './sections/Banner';
import { SparkRewardMultiply } from './sections/Multiply';
import { SparkRewardRanking } from './sections/Ranking';
import { SparkRewardReward } from './sections/Reward';
import { SparkRewardRules } from './sections/Rules';
import { SparkRewardShare } from './sections/Share';
import { SparkRewardSummary } from './sections/Summary';
import { SparkRewardTrade } from './sections/Trade';

export const SparkRewardPage = ({
  ended,
  sparkCoinImageUrl,
  banner,
  share,
  summary,
  ranking,
  trade,
  multiply,
  reward,
  rules,
}: {
  ended?: boolean;
  sparkCoinImageUrl: string;
  banner: Omit<ComponentProps<typeof SparkRewardBanner>, 'css'>;
  share?: Omit<ComponentProps<typeof SparkRewardShare>, 'css'>;
  summary?: Omit<ComponentProps<typeof SparkRewardSummary>, 'css'>;
  ranking?: Omit<ComponentProps<typeof SparkRewardRanking>, 'css'>;
  trade: Omit<ComponentProps<typeof SparkRewardTrade>, 'css'>;
  multiply: Omit<ComponentProps<typeof SparkRewardMultiply>, 'css'>;
  reward: Omit<ComponentProps<typeof SparkRewardReward>, 'css'>;
  rules?: Omit<ComponentProps<typeof SparkRewardRules>, 'css'>;
}) => {
  return (
    <SparkRewardPageLayout ended={ended} sparkCoinImageUrl={sparkCoinImageUrl}>
      <SparkRewardBanner css={css.raw({ gridColumn: ['container', 'content'] })} {...banner} />
      {!ended && share && <SparkRewardShare css={css.raw({ gridColumn: ['container'], mt: 40, mb: 40 })} {...share} />}
      {summary && <SparkRewardSummary css={css.raw({ gridColumn: 'content', mb: 20 })} {...summary} />}
      {ranking && <SparkRewardRanking css={css.raw({ gridColumn: 'content', mb: [40, 100] })} {...ranking} />}
      <SparkRewardTrade
        css={css.raw({ gridColumn: 'content', mb: [40, 100], _firstOfType: { mt: [40, 100] } })}
        {...trade}
      />
      <SparkRewardMultiply css={css.raw({ gridColumn: 'content', mb: [40, 100] })} {...multiply} />
      <SparkRewardReward css={css.raw({ gridColumn: 'content' })} {...reward} />
      {!ended && share && <SparkRewardShare css={css.raw({ gridColumn: ['container'], mt: 40 })} {...share} />}
      <SparkRewardRules css={css.raw({ gridColumn: 'full', mt: [56, 180] })} {...rules} />
    </SparkRewardPageLayout>
  );
};

interface SparkRewardPageLayoutProps {
  ended?: boolean;
  sparkCoinImageUrl: string;
  children?: ReactNode;
}
const SparkRewardPageLayout = ({ ended, sparkCoinImageUrl, children }: SparkRewardPageLayoutProps) => (
  <div className={vstack({ bg: 'inverse.black' })}>
    {ended && <ComingSoon sparkCoinImageUrl={sparkCoinImageUrl} />}
    <div
      style={ended ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
      className={css({
        display: 'grid',
        gridTemplateColumns: [
          '[full-start] 0 [container-start] 16px [content-start] auto [content-end] 16px [container-end] 0 [full-end]',
          '[full-start] 1fr [container-start] 16px [content-start] minmax(0, 960px) [content-end] 16px [container-end] 1fr [full-end]',
        ],
        gap: 0,
      })}
    >
      {children}
    </div>
  </div>
);

interface ComingSoonProps {
  sparkCoinImageUrl: string;
}
const ComingSoon = ({ sparkCoinImageUrl }: ComingSoonProps) => {
  return (
    <div
      className={css({ pt: { base: 24, tablet: 64 }, pb: 24, px: 16, w: '100%', maxWidth: 992, textAlign: 'center' })}
    >
      <div className={vstack({ mb: 64, py: 24, borderRadius: 8, bg: 'background.default.level-1' })}>
        <h1 className={css({ mb: 4, textStyle: 'Display/XL' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Sparks_Season_2' />
        </h1>
        <h2 className={css({ textStyle: 'Title/S' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Coming_soon' />
        </h2>
        <img alt='' src={sparkCoinImageUrl} className={css({ my: 12, pointerEvents: 'none' })} />
        <p className={css({ textStyle: 'Text/M/Regular', maxW: 315 })}>
          <FormattedMessage id='Autogen_common.r251126.s_We_re_gearing_up_for_even_more_exciting_opportunities_in_Season_2_You_won_t_be_disappointed_stay_tuned' />
        </p>
      </div>
      <div className={vstack()}>
        <h2 className={css({ mb: 8, textStyle: 'Title/M' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Sparks_Season_1_has_ended' />
        </h2>
        <p className={css({ textStyle: 'Text/M/Regular', maxW: 350 })}>
          <FormattedMessage id='Autogen_common.r251126.s_We_hope_you_ve_enjoyed_our_Spark_events_including_the_Sparks_Launchpool_and_Sparks_Auction' />
        </p>
      </div>
    </div>
  );
};
